import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Plus, Mail, Pencil, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const TRIGGERS = [
  { value: 'manual', label: 'Manual (send on demand)' },
  { value: 'new_student', label: 'New student enrolled' },
  { value: 'new_parent', label: 'New parent registered' },
  { value: 'new_teacher', label: 'New teacher added' },
];

const PLACEHOLDERS = ['{{student_name}}', '{{parent_name}}', '{{teacher_name}}', '{{school_name}}', '{{grade}}', '{{class_name}}', '{{admission_number}}'];

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', subject: '', body: '', trigger: 'manual', is_active: true });

  const loadAll = () => {
    api.get('/admin/email-templates').then(r => setTemplates(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', subject: '', body: '', trigger: 'manual', is_active: true });
    setShowDialog(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, subject: t.subject, body: t.body, trigger: t.trigger || 'manual', is_active: t.is_active !== false });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name || !form.subject || !form.body) { toast.error('Name, subject, and body are required'); return; }
    try {
      if (editing) {
        await api.put(`/admin/email-templates/${editing.id}`, form);
        toast.success('Template updated');
      } else {
        await api.post('/admin/email-templates', form);
        toast.success('Template created');
      }
      setShowDialog(false);
      loadAll();
    } catch { toast.error('Failed to save template'); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/admin/email-templates/${id}`);
      toast.success('Template deleted');
      loadAll();
    } catch { toast.error('Failed to delete'); }
  };

  const previewTemplate = async (id) => {
    try {
      const { data } = await api.post(`/admin/email-templates/${id}/preview`);
      setPreviewData(data);
      setShowPreview(true);
    } catch { toast.error('Failed to load preview'); }
  };

  return (
    <div data-testid="email-templates">
      <h1 className="text-2xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-2">
        <Mail className="w-6 h-6" /> Email Templates
      </h1>
      <p className="text-sm text-slate-500 mb-6">Create and manage email templates for welcoming new students, parents, and teachers. Email sending will be connected to a provider in a future update.</p>

      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{templates.length} template{templates.length !== 1 ? 's' : ''}</p>
        <Button onClick={openNew} className="bg-[#1B2A4A]" data-testid="add-template-btn">
          <Plus className="w-4 h-4 mr-1" /> New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">No email templates yet. Create one to get started.</CardContent></Card>
      ) : (
        <div className="bg-white rounded-xl border shadow-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="data-table">
                <TableHead>Name</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map(t => (
                <TableRow key={t.id}>
                  <TableCell className="font-semibold text-[#1B2A4A] text-sm">{t.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{t.subject}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs capitalize">{t.trigger?.replace('_', ' ')}</Badge></TableCell>
                  <TableCell>
                    <Badge className={t.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                      {t.is_active !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => previewTemplate(t.id)} title="Preview">
                        <Eye className="w-4 h-4 text-sky-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}>
                        <Pencil className="w-4 h-4 text-[#1B2A4A]" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteTemplate(t.id)} data-testid={`delete-template-${t.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Template Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit Template' : 'New Template'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template Name</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Welcome New Student" data-testid="template-name-input" />
            </div>
            <div>
              <Label>Trigger</Label>
              <Select value={form.trigger} onValueChange={v => setForm({...form, trigger: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TRIGGERS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email Subject</Label>
              <Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="Welcome to Tadam Academy, {{student_name}}!" data-testid="template-subject-input" />
            </div>
            <div>
              <Label>Email Body</Label>
              <Textarea value={form.body} onChange={e => setForm({...form, body: e.target.value})} rows={6} placeholder="Dear {{parent_name}},&#10;&#10;We are delighted to welcome {{student_name}} to {{school_name}}..." data-testid="template-body-input" />
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-1.5">Available Placeholders</p>
              <div className="flex flex-wrap gap-1.5">
                {PLACEHOLDERS.map(p => (
                  <button key={p} type="button" onClick={() => setForm({...form, body: form.body + p})} className="text-xs bg-white border rounded px-2 py-0.5 text-[#1B2A4A] hover:bg-amber-50 hover:border-[#E8A830] transition-colors">
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#1B2A4A]" data-testid="save-template-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Email Preview</DialogTitle></DialogHeader>
          {previewData && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700 font-semibold">Preview only - emails are not sent yet</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-semibold uppercase">Subject</p>
                <p className="font-bold text-[#1B2A4A] mt-1">{previewData.subject}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-semibold uppercase">Body</p>
                <div className="bg-white border rounded-lg p-4 mt-1 text-sm text-slate-700 whitespace-pre-wrap">{previewData.body}</div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
